/**
 * School Mobile App Assets API Contract
 *
 * POST /v1/schools/:school_id/mobile-app-assets/upload-signature
 * POST /v1/schools/:school_id/mobile-app-assets/:slotName/commit
 *
 * The WRITE half of the pre-login imagery lane: a per-school admin uploads the
 * three onboarding slide backgrounds and the login background from Manage App
 * to Design to Onboarding, and they land as `MobileAppAssetSlot` rows on the
 * school's own `MobileApp`.
 *
 * Why school-scoped endpoints rather than reusing the mobile-apps ones: every
 * `/v1/mobile-apps/*` asset write is super-admin gated (a fleet surface keyed
 * on a build identifier the web dashboard has no reason to know). These two
 * routes answer "change MY school's pre-login images" for a per-school admin,
 * resolving the `MobileApp` row from the school rather than trusting an id in
 * the request.
 *
 * Deliberately NOT reachable here: any slot outside
 * `SCHOOL_EDITABLE_ASSET_SLOTS`. Build slots (`app-icon`, `splash`,
 * `adaptive-icon-foreground`) and the logo slots are rejected as
 * `SLOT_NOT_EDITABLE` before the shared core runs, so a school surface can
 * never write a build identity. The request/response shapes are the super-admin
 * ones verbatim, so the two lanes cannot drift apart on the wire.
 */

import { z } from 'zod'
import {
  AssetCommitRequestSchema,
  AssetUploadSignatureRequestSchema,
  MobileAppAssetSlotSchema,
  SchoolAssetUploadSignatureResponseSchema
} from '../../schemas/entities/mobile-app-asset'

// --- Upload signature ---

/** Body: `{ slotName }`, checked against the editable allowlist by the use-case. */
export const SchoolMobileAppAssetUploadSignatureRequestSchema = AssetUploadSignatureRequestSchema
export type SchoolMobileAppAssetUploadSignatureRequest = z.infer<
  typeof SchoolMobileAppAssetUploadSignatureRequestSchema
>

/** The Cloudinary signed-widget parameters: the super-admin lane's, plus the school guard and signed tags. */
export const SchoolMobileAppAssetUploadSignatureResponseSchema = SchoolAssetUploadSignatureResponseSchema
export type SchoolMobileAppAssetUploadSignatureResponse = z.infer<
  typeof SchoolMobileAppAssetUploadSignatureResponseSchema
>

// --- Commit ---

/**
 * The uploaded asset's coordinates. On this lane `width`, `height`, `mimeType`
 * and `sizeBytes` are re-derived from Cloudinary rather than trusted from the
 * body (KTD18); the shape stays identical so the same client code can post to
 * either lane.
 */
export const SchoolMobileAppAssetCommitRequestSchema = AssetCommitRequestSchema
export type SchoolMobileAppAssetCommitRequest = z.infer<typeof SchoolMobileAppAssetCommitRequestSchema>

/** The committed slot row, as the super-admin commit endpoint returns it. */
export const SchoolMobileAppAssetCommitResponseSchema = MobileAppAssetSlotSchema
export type SchoolMobileAppAssetCommitResponse = z.infer<typeof SchoolMobileAppAssetCommitResponseSchema>

// --- Errors ---

/**
 * Failure vocabulary for both routes. `SLOT_NOT_EDITABLE` is the allowlist
 * refusal (a real slot name a school may not write); `ASSET_NOT_IN_SCOPE` and
 * `ASSET_NOT_FOUND` are the commit-time Cloudinary checks that stop one school
 * from pointing a slot at another tenant's upload.
 */
export const SCHOOL_MOBILE_APP_ASSET_ERROR_CODES = [
  'INVALID_PARAMS',
  'NOT_AUTHORIZED',
  'NO_MOBILE_APP',
  'SLOT_NOT_EDITABLE',
  'ASSET_NOT_IN_SCOPE',
  'ASSET_NOT_FOUND',
  'RATE_LIMITED',
  'CONFIGURATION_ERROR',
  'VALIDATION_FAILED',
  'INTERNAL_ERROR'
] as const
export type SchoolMobileAppAssetErrorCode = (typeof SCHOOL_MOBILE_APP_ASSET_ERROR_CODES)[number]

/**
 * The ONLY text a school admin sees for a backend configuration error (KTD19).
 * The underlying detail (missing Cloudinary credentials, an unprovisioned
 * registry row) is logged server-side and never rendered: it names internal
 * infrastructure and a school admin can do nothing with it.
 */
export const SCHOOL_ASSET_UPLOAD_UNAVAILABLE_MESSAGE =
  'Image uploads are not available for this school yet. Contact support.'

/**
 * Freshness expectation shown beside the pre-login drop zones. The image is
 * fetched by a background refresh and only rendered once it is on disk, so the
 * first open after an upload still shows the old image by design.
 */
export const PRE_LOGIN_IMAGE_FRESHNESS_COPY =
  'Shows on the next app open after the images finish downloading in the background (usually the second open), on app version 1.9 and later.'

/**
 * The slot allowlist and its guard, re-exported here so a school-facing client
 * can import everything it needs for this lane from one subpath. The values
 * live with the slot inventory in `schemas/entities/mobile-app-asset.ts`.
 */
export { SCHOOL_EDITABLE_ASSET_SLOTS, isSchoolEditableAssetSlotName } from '../../schemas/entities/mobile-app-asset'
export type { SchoolEditableAssetSlotName } from '../../schemas/entities/mobile-app-asset'
